import { Injectable, Inject } from '@angular/core';
import { HttpInterceptorService } from '@ispm/core/services/http-interceptor.service';
import { Observable } from 'rxjs/Observable';
import { IgenomicsState } from '@ispm/core/interfaces/genomics-state.interface';
import { createSelector, MemoizedSelector, select, Store } from '@ngrx/store';
import { InterpolateService } from '@ispm/shared/services/interpolate.service';
import { IDiagnosticOrder } from '../interfaces/diagnostic-order.interface';
import { TestService } from './test.service';
import { CoreActionCreatorService } from '@ispm/core/services/core-action-creator.service';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import * as immutable from 'immutable';

@Injectable()
export class TestActionsService {

  static URLSuffix = '/test';
  static QMSPattern = '$(QMS_ROOT)/$(QMS_REPORT_ID)$(TEST_NUMBER)';

  constructor(
    private _http: HttpInterceptorService,
    private store: Store<IgenomicsState>,
    private authService: AuthenticationService,
    private notificationMessageService: NotificationMessageService,
    private testService: TestService, // setted here for initialize the TestService class, do not remove
    private interpolateService: InterpolateService,
    private coreAc: CoreActionCreatorService
  ) {
  }

  AddSpecimen(specimen: any, testId: string, limsWorkflow: string, revisionId: string, workflowRequestTemplateId: string,
              skipLims?: boolean,validateSpecimens:boolean = true) {
    return this._http.post(`${TestActionsService.URLSuffix}/createSpecimen`,
      {
        specimen: [specimen],
        testId: testId,
        limsWorkflowId: limsWorkflow,
        workflowRequestTemplateId: workflowRequestTemplateId,
        organization: this.authService.user.organization,
        revisionId: revisionId,
        skipLims: skipLims === true,
        validateSpecimens: validateSpecimens
      }
      , false).pipe(
        map(res => res.json()),
        map(({ data, message }) => {
          this.notificationMessageService.success('Sample Saved Successfully');

          if (message) {
            setTimeout(() => this.notificationMessageService.success(message), 3000);
          }

          return data;
        })
      )

      .catch(err => {
        this.notificationMessageService.error('Unable to Save Sample');
        console.warn(err);
        switch (err.status) {
          case 420:
            return Observable.of(err.error.data);
          default:
            return Observable.throw(err.message);
        }

      });
  }

  UpdateSpecimen(specimen: any) {
    this.coreAc.setLoader(true);
    return this._http.put(`${TestActionsService.URLSuffix}/updateSpecimen`, specimen, false)
      .map(res => res.json())
      .map(res => {
        if (!!res.type) {
          this.notificationMessageService.success('Specimen updated Successfully');
        }
        return res;
      })
      .catch(err => {
        this.notificationMessageService.error('Unable to updated Specimen');
        console.warn(err);
        this.coreAc.setLoader(false);
        return Observable.empty();
      });
  }

  markSpecimenAsPrimary(body: any) {
    this.coreAc.setLoader(true);
    return this._http.put(`${TestActionsService.URLSuffix}/markSpecimenAsPrimary`, body, false)
      .map(res => res.json())
      .map(res => {
        return res;
      })
      .catch(err => {
        this.notificationMessageService.error('Unable to mark specimen as primary');
        console.warn(err);
        this.coreAc.setLoader(false);
        return Observable.empty();
      });
  }

  updateNonConformanceStatus(workflowId: string, revisionId: string, alertsIds: string[]): Observable<any> {
    return this._http.post(`${TestActionsService.URLSuffix}/updateNonConformanceStatus`,
      { workflowId, revisionId, alertsIds}, true)
      .map(res => {
        this.notificationMessageService.success('QMS status updated');
        return res.json();
      })
      .catch(err => {
        this.notificationMessageService.error('Unable to updated QMS status');
        return Observable.throw(err);
      });
  }

  getAlerts(diagnosticOrderId: string): Observable<any> {
    return this._http.post(`${TestActionsService.URLSuffix}/getAlerts`, { diagnosticOrderId})
      .map(res => {
        return res.json();
      })
      .catch(err => {
        this.notificationMessageService.error('Unable to get test alerts');
        return Observable.throw(err);
      });
  }

  openQMS(testNumber) {
    const windowInstance = window.open();
    this.store.select('users', 'organizationConfiguration', 'qms')
      .take(1)
      .catch((err) => {
        windowInstance.close();
        return Observable.empty();
      })
      .subscribe((data: { root: string, report_id: string }) => {
        if (data) {
          const url = this.interpolateService.interpolate(TestActionsService.QMSPattern, {
            QMS_ROOT: data.root,
            QMS_REPORT_ID: data.report_id,
            TEST_NUMBER: testNumber
          });
          windowInstance.location.assign(url);
        } else {
          windowInstance.close();
        }
      });
  }

  getStateByPath<T = any | IgenomicsState>(path: Array<string>): Observable<T | undefined> {
    const selectStateByProp = (prop: string) => (state: any) => {
      if (state instanceof immutable.Map || state instanceof immutable.List) {
        return (state as any).get(prop);
      }
      return state?.[prop];
    };

    const createPathChainedSelectors = (segments: string[]) =>
      segments.reduce(
        (selector: MemoizedSelector<any, any>, segment) => createSelector(
          selector,
          selectStateByProp(segment),
        ),
        store => store,
      );

    return this.store.pipe(
      select(createPathChainedSelectors(path)),
      distinctUntilChanged(),
    );
  }

  updateTestInfo(testInfo: IDiagnosticOrder, revisionId: string, updateResourceName: string, updatedFields: string[]): Observable<IDiagnosticOrder> {
    return this._http.put(`${TestActionsService.URLSuffix}/updateTestInfo`, {
      testInfo: testInfo,
      revisionId: revisionId,
      updateResourceName: updateResourceName,
      propertyName: Array.from(updatedFields)
    })
      .map(res => res.body)
      .catch(err => {
        if (err.data) {
          this.notificationMessageService.error(err.data);
        } else {
          this.notificationMessageService.error('error while updating test information');
        }
        return Observable.of(null);
      });
  }
}
